import type {
  PushJobItemStatus,
  PushJobMode,
  PushJobStatus,
  PushSendErrorCode,
  PushSkipReason
} from "@tg/shared";

// Человекочитаемые подписи для отчётов ИИ-пропуша (модалка и журнал).

export const PUSH_SKIP_LABELS: Record<PushSkipReason, string> = {
  not_private: "не личный чат (группа/канал)",
  bot: "бот",
  self: "свой аккаунт",
  no_messages: "нет сообщений",
  last_message_not_ours: "последнее сообщение не наше",
  not_read_yet: "юзер ещё не прочитал",
  hydration_failed: "не удалось загрузить чат",
  user_replied: "юзер ответил до отправки",
  last_message_changed: "чат изменился после скана",
  cancelled: "отменено",
  empty_context: "нет текста для контекста",
  llm_empty: "ИИ не дал текст",
  moderation: "текст не прошёл антифрод"
};

export const PUSH_ERROR_LABELS: Record<PushSendErrorCode, string> = {
  flood_wait: "ограничение Telegram (FLOOD_WAIT)",
  user_blocked: "юзер заблокировал аккаунт",
  peer_invalid: "чат недоступен",
  write_forbidden: "писать запрещено",
  user_deactivated: "аккаунт юзера удалён",
  llm_error: "ошибка ИИ",
  unknown_outcome: "нет ответа при отправке",
  other: "другая ошибка"
};

export const PUSH_STATUS_LABELS: Record<PushJobStatus, string> = {
  queued: "В очереди",
  scanning: "Сканируем папку…",
  sending: "Отправляем…",
  paused: "Пауза (ограничение Telegram)",
  done: "Завершён",
  cancelled: "Отменён",
  failed: "Остановлен с ошибкой"
};

export const PUSH_ITEM_STATUS_LABELS: Record<PushJobItemStatus, string> = {
  pending: "в очереди",
  generating: "ИИ пишет…",
  sending: "отправляем…",
  sent: "отправлено",
  skipped: "пропущено",
  error: "ошибка"
};

// Для пуша скриптами «ИИ пишет…» неверно — текст берётся из заготовок.
export const pushItemStatusLabel = (status: PushJobItemStatus, mode: PushJobMode): string =>
  mode === "scripts" && status === "generating" ? "готовим текст…" : PUSH_ITEM_STATUS_LABELS[status];

export const pushModeLabel = (mode: PushJobMode | undefined): string =>
  mode === "scripts" ? "Скрипты" : "ИИ";

export const isActivePushStatus = (status: PushJobStatus): boolean =>
  status === "scanning" || status === "sending" || status === "paused";

/** Ещё не завершён: в работе или ждёт в очереди. */
export const isUnfinishedPushStatus = (status: PushJobStatus): boolean =>
  status === "queued" || isActivePushStatus(status);

// Крупное состояние папки в очереди пушей (то, что просил заказчик):
// «В работе» / «В очереди» / «Завершено»; отмена и ошибка — отдельно.
export const pushQueueStateLabel = (status: PushJobStatus): string => {
  switch (status) {
    case "queued":
      return "В очереди";
    case "scanning":
    case "sending":
    case "paused":
      return "В работе";
    case "done":
      return "Завершено";
    case "cancelled":
      return "Отменено";
    case "failed":
      return "Остановлено с ошибкой";
  }
};

export const formatReasonCounts = <K extends string>(
  counts: Partial<Record<K, number>>,
  labels: Record<K, string>
): Array<{ label: string; count: number }> =>
  (Object.entries(counts) as Array<[K, number | undefined]>)
    .filter((entry): entry is [K, number] => typeof entry[1] === "number" && entry[1] > 0)
    .sort((left, right) => right[1] - left[1])
    .map(([key, count]) => ({ label: labels[key] ?? key, count }));
